import React from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { Sheet, SheetContent } from '@/shared/widgets/sheet';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cn } from '@/shared/utils/cn';
import type { ListingFilter } from '../../domain/models/Filter';

interface PlpFilterSheetProps {
  open: boolean;
  filters: ListingFilter[];
  selectedOptions: Record<string, string[]>;
  applyUrl: string | null;
  isLoadingPreview: boolean;
  filtersResetUrl?: string;
  onToggleOption: (category: string, name: string, url: string) => void;
  onApply: () => void;
  onReset: () => void;
  onClose: () => void;
  /** Analytics: fires when a filter item is toggled; receives category + displayName */
  onFilterItemToggle?: (category: string, displayName: string) => void;
  /** Analytics: fires when a filter accordion category is first opened */
  onFilterCategoryExpand?: (category: string) => void;
}

export function PlpFilterSheet({
  open,
  filters,
  selectedOptions,
  onToggleOption,
  onApply,
  onReset,
  onClose,
  onFilterItemToggle,
  onFilterCategoryExpand,
}: PlpFilterSheetProps) {
  const openCategoriesRef = React.useRef<Set<string>>(new Set());
  const totalActiveCount = Object.values(selectedOptions).reduce(
    (sum, names) => sum + names.length,
    0
  );


  return (
    <Sheet open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <SheetContent
        side="bottom"
        className="left-0 right-0 bottom-0 h-[100dvh] max-h-[100dvh] rounded-none p-0 flex flex-col"
        showCloseButton={false}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3.5 shrink-0">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Close filters"
              className="-ml-1 p-1"
              onClick={onClose}
            >
              <ArrowLeft className="h-5 w-5 text-gray-900" />
            </button>
            <h2 className="text-[17px] font-semibold text-gray-900">
              Filters{totalActiveCount > 0 ? ` (${totalActiveCount})` : ''}
            </h2>
          </div>
          <button
            type="button"
            className="border border-gray-900 px-3 py-1 text-[12px] font-semibold uppercase tracking-wider text-gray-900"
            onClick={onReset}
          >
            Reset
          </button>
        </div>

        {/* Scrollable filter list */}
        <div className="flex-1 overflow-y-auto">
          <AccordionPrimitive.Root
            type="multiple"
            defaultValue={[]}
            onValueChange={(openValues) => {
              const prevOpen = openCategoriesRef.current;
              const newlyOpened = openValues.filter((v) => !prevOpen.has(v));
              newlyOpened.forEach((category) => onFilterCategoryExpand?.(category));
              openCategoriesRef.current = new Set(openValues);
            }}
          >
            {filters.map((filter) => {
              const selectedInCategory = selectedOptions[filter.category] ?? [];

              return (
                <AccordionPrimitive.Item
                  key={filter.category}
                  value={filter.category}
                  className="border-b border-gray-200"
                >
                  <AccordionPrimitive.Header>
                    <AccordionPrimitive.Trigger className="group flex w-full items-center justify-between px-4 py-4 text-left">
                      <div className="min-w-0 pr-4">
                        <p className="text-[15px] font-bold text-gray-900">{filter.category}</p>
                        {selectedInCategory.length > 0 ? (
                          <p className="mt-0.5 truncate text-[13px] text-gray-500">
                            {selectedInCategory
                              .map(
                                (name) =>
                                  filter.items.find((i) => i.name === name)?.displayName ?? name
                              )
                              .join(', ')}
                          </p>
                        ) : (
                          filter.items.length > 0 && filter.items[0].displayName && (
                            <p className="mt-0.5 truncate text-[13px] text-gray-400 italic">
                              {`E.g. ${filter.items.slice(0, 2).map(i => i.displayName).join(', ')}${filter.items.length > 2 ? '..' : ''}`}
                            </p>
                          )
                        )}
                      </div>
                      <span
                        aria-hidden
                        className="shrink-0 text-[22px] leading-none text-gray-500 group-data-[state=open]:hidden"
                      >
                        +
                      </span>
                      <span
                        aria-hidden
                        className="shrink-0 text-[22px] leading-none text-gray-500 group-data-[state=closed]:hidden"
                      >
                        −
                      </span>
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <ul className="px-4 pb-2">
                      {filter.items.map((item) => {
                        const isSelected = selectedInCategory.includes(item.name);
                        return (
                          <li key={item.name} className="border-b border-gray-100 last:border-0">
                            <button
                              type="button"
                              className="flex w-full items-center gap-3 py-3 text-left"
                              onClick={() => {
                                onFilterItemToggle?.(filter.category, item.displayName);
                                onToggleOption(filter.category, item.name, item.url);
                              }}
                            >
                              {/* Circular checkbox */}
                              <div
                                className={cn(
                                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
                                  isSelected
                                    ? 'border-[#3F84AD] bg-[#3F84AD]'
                                    : 'border-[#3F84AD] bg-white'
                                )}
                              >
                                {isSelected && (
                                  <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                                )}
                              </div>
                              <span
                                className={cn(
                                  'text-[14px]',
                                  isSelected ? 'font-bold text-gray-900' : 'text-gray-700'
                                )}
                              >
                                {item.displayName}
                                {item.count > 0 && (
                                  <span className="font-normal"> ({item.count})</span>
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
              );
            })}
          </AccordionPrimitive.Root>
        </div>

        {/* Footer */}
        <div className="shrink-0">
          <button
            type="button"
            className="w-full bg-[--primary-color] py-4 text-[15px] font-semibold uppercase tracking-widest text-white"
            onClick={onApply}
          >
            Apply
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
